<template>
  <VLayout row :wrap="['sm', 'xs'].includes($mq)" class="material-usage">
    <VFlex
      class="flex-item field-wrap daily-list-item--title material-picklist"
    >
      <BasePicklist
        v-if="
          useJobInventory && jobInventoryItems && jobInventoryItems.length > 0
        "
        :disabled="disabled"
        :title="displayTitle"
        :value="currentValue"
        :items="inventoryPickerItems"
        item-text="JOB_INVENTORY_ITEM_NAME"
        item-value="ID"
        :hint="pickerHint"
        :no-data-text="`No Job Inventory Items found for this job`"
        :rules="[(v) => !!v || 'You must select a Job Inventory Item']"
        v-on="$listeners"
        @input="handleJobInventorySelection($event)"
      />
      <BasePicklist
        v-else
        :disabled="disabled"
        :title="asset.MATERIAL_NAME"
        :value="asset.MATERIAL_NAME"
        :items="materials"
        item-text="MATERIALPART_NAME"
        hint="Material/Part"
        :no-data-text="
          `No Materials found for Job Type '${getLocation.JOB_TYPE}'`
        "
        :rules="[(v) => !!v || 'You must select a Material/Part']"
        v-on="$listeners"
        @input="handleMaterialSelection($event)"
      />
    </VFlex>
    <VFlex
      class="flex-11-print flex-11-sm flex-11-md flex-1-xs flex-item field-wrap"
    >
      <ListItemDisplayOnly :value="unitType" hint="Unit" />
    </VFlex>
    <VFlex :class="flexStyles">
      <ListItemText
        :disabled="disabled"
        :value="asset.MATERIAL_NOTES"
        hint="Notes"
        @input="$emit('update', { MATERIAL_NOTES: $event })"
        @focus="$emit('startSaveTimer')"
      />
    </VFlex>
    <VFlex
      class="flex-10-print flex-10-sm flex-10-md flex-1-xs flex-item field-wrap"
    >
      <ListItemText
        :disabled="disabled"
        :value="asset.QUANTITY_SENT_OUT"
        hint="Sent"
        :suffix="unitType"
        :rules="[isNumber]"
        @input="$emit('update', { QUANTITY_SENT_OUT: Number($event) })"
        @focus="$emit('startSaveTimer')"
      />
    </VFlex>
    <VFlex
      class="flex-10-print flex-10-sm flex-10-md flex-1-xs flex-item field-wrap"
    >
      <span
        v-if="isExcludedFromBilling"
        class="ml-2 billing-exclusion text-center"
        >Not Billable</span
      >
      <ListItemText
        v-else
        :disabled="disabled || isExcludedFromBilling"
        :value="asset.QUANTITY_USED"
        hint="Used"
        :suffix="unitType"
        :rules="[isNumber]"
        @input="$emit('update', { QUANTITY_USED: Number($event) })"
        @focus="$emit('startSaveTimer')"
      />
    </VFlex>
    <VFlex
      class="flex-10-print flex-10-sm flex-10-md flex-1-xs flex-item field-wrap checkbox-col"
    >
      <ListItemCheckbox
        :disabled="disabled"
        :value="asset.INCLUDE_IN_TIME__MATERIALS_BILLING"
        hide-details
        class="ml-1"
        label="T&amp;M?"
        @change="
          $emit('update', { INCLUDE_IN_TIME__MATERIALS_BILLING: $event })
        "
      />
    </VFlex>
    <VFlex
      class="flex-10-print flex-10-sm flex-10-md flex-1-xs flex-item field-wrap checkbox-col"
    >
      <ListItemCheckbox
        :disabled="disabled"
        :value="asset.IS_SPECIAL_MATERIAL"
        hide-details
        class="ml-1"
        label="Special Material?"
        print-icon="star_outline"
        @change="$emit('update', { IS_SPECIAL_MATERIAL: $event })"
      />
    </VFlex>
  </VLayout>
</template>

<script>
import _get from 'lodash/get'
import { mapGetters } from 'vuex'
import FlexStylesMixin from '@mixins/flex-styles'

export default {
  name: 'AssignmentMaterial',
  mixins: [FlexStylesMixin(7)],
  props: {
    asset: {
      type: Object,
      required: true,
      default: () => {},
    },
    materials: {
      type: Array,
      required: true,
      default: () => [],
    },
    jobInventoryItems: {
      type: Array,
      default: () => [],
    },
    useJobInventory: {
      type: Boolean,
      default: false,
    },
    draft: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      isNumber: (v) => {
        return !isNaN(Number(v)) || 'You must enter a number'
      },
    }
  },
  computed: {
    ...mapGetters(['getLocation', 'isPrintModeOn']),

    inventoryPickerItems() {
      // Include the Misc./Unclassified option if needed
      return this.jobInventoryItems || []
    },

    displayTitle() {
      if (this.useJobInventory) {
        // If using job inventory, show the selected job inventory item name
        const jobInventoryId = _get(this.asset, 'JOB_INVENTORY_ITEM[0].id')
        if (jobInventoryId) {
          const item = this.jobInventoryItems.find(
            (i) => i.ID === jobInventoryId
          )
          return _get(item, 'JOB_INVENTORY_ITEM_NAME', '')
        } else {
          let name = this.asset.MATERIAL_NAME
          name = this.asset.new ? name : '⚠️ Inv Item Missing - ' + name
          return name
        }
      }
      return this.asset.MATERIAL_NAME
    },

    currentValue() {
      if (this.useJobInventory) {
        return _get(this.asset, 'JOB_INVENTORY_ITEM[0].id')
      }
      return this.asset.MATERIAL_NAME
    },

    pickerHint() {
      return this.useJobInventory ? 'Job Inventory Item' : 'Material/Part'
    },

    unitType() {
      let type = _get(this.asset, 'UNIT_TYPE')

      if (this.useJobInventory && _get(this.draft, 'JOB_INVENTORY_ITEM')) {
        // If using job inventory, get unit type from the associated material
        let newId = _get(this.draft, 'JOB_INVENTORY_ITEM[0].id')
        let inventoryItem = this.jobInventoryItems.find((i) => i.ID === newId)
        if (inventoryItem && inventoryItem.MATERIALPART) {
          let materialId = _get(inventoryItem, 'MATERIALPART[0].id')
          let material = this.materials.find((m) => m.ID === materialId)
          type = _get(material, 'UNIT_TYPE', type)
        }
      } else if (_get(this.draft, 'MATERIALPART')) {
        let newId = _get(this.draft, 'MATERIALPART[0].id')
        let newMaterial = this.materials.find((m) => m.ID === newId)
        type = _get(newMaterial, 'UNIT_TYPE')
      }

      return type
    }, // unitType

    selectedInventoryItem() {
      if (!this.useJobInventory) return null
      const jobInventoryId = _get(this.draft, 'JOB_INVENTORY_ITEM[0].id')
        || _get(this.asset, 'JOB_INVENTORY_ITEM[0].id')
      if (!jobInventoryId) return null
      return this.jobInventoryItems.find((i) => i.ID === jobInventoryId) || null
    },

    isExcludedFromBilling() {
      if (!this.useJobInventory) return false
      return parseInt(_get(this.selectedInventoryItem, 'EXCLUDE_FROM_BILLING')) === 1
    },

    disabled() {
      return this.draft.isWorking || false
    }, // disabled
  },

  methods: {
    handleMaterialSelection(materialId) {
      const material = this.materials.find((m) => m.ID === materialId)

      if (!material) {
        console.error('Selected material not found')
        return
      }

      this.$emit('update', {
        MATERIALPART: [
          {
            id: materialId,
            identifier: material.MATERIALPART_NAME,
          },
        ],
      })
    },

    handleJobInventorySelection(inventoryItemId) {
      const inventoryItem = this.jobInventoryItems.find(
        (item) => item.ID === inventoryItemId
      )

      if (!inventoryItem) {
        console.error('Selected job inventory item not found')
        return
      }

      // Get the associated material/part from the inventory item
      const materialPartId = _get(inventoryItem, 'MATERIALPART[0].id')
      const materialPartIdentifier = _get(
        inventoryItem,
        'MATERIALPART[0].identifier'
      )

      // Update both the job inventory item and the derived material/part
      const updatePayload = {
        JOB_INVENTORY_ITEM: [
          {
            id: inventoryItemId,
            identifier: inventoryItem.JOB_INVENTORY_ITEM_NAME,
          },
        ],
      }

      // Only set MATERIALPART if it exists on the inventory item
      if (materialPartId) {
        updatePayload.MATERIALPART = [
          {
            id: materialPartId,
            identifier: materialPartIdentifier || '',
          },
        ]
      }

      this.$emit('update', updatePayload)
    },
  },
}
</script>

<style lang="scss">
.material-picklist .v-input {
  label,
  input {
    font-size: 13px;
    color: black;
    text-transform: uppercase;
  }
}
.daily-list-item.special-material {
  border: 2px solid #2196f3 !important;
  @media print {
    border-color: black !important;
    border-width: 2px 0 !important;
  }
}
.billing-exclusion {
  font-size: 11px;
  font-style: italic;
  color: lighten(black, 40%);
}
</style>
